// ============================================================================
// DATA VALIDATION SERVICE
// ============================================================================
// Service for validating compliance and emissions data
// Now uses API client instead of local storage
// ============================================================================

import { dataValidationApi } from '@domains/compliance/dataValidation.api';

// ============================================================================
// TYPES
// ============================================================================

export interface ValidationResult {
  rule_id: string;
  record_id: number;
  field_name: string;
  status: 'pass' | 'warning' | 'fail' | 'error';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  suggested_action?: string;
  timestamp: string;
  rule_name?: string;
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface ValidationWarning {
  field: string;
  message: string;
  code: string;
}

export interface ValidationRule {
  id: string;
  name: string;
  description: string;
  type: ValidationType;
  field_types: string[];
  parameters: Record<string, any>;
  is_active: boolean;
  severity: ValidationSeverity;
  created_at: string;
  updated_at: string;
}

export interface AnomalyDetection {
  id: string;
  type: AnomalyType;
  description: string;
  severity: ValidationSeverity;
  confidence: number;
  detected_at: string;
  resolved_at?: string;
  resolution_notes?: string;
  affected_records: string[];
  metrics?: {
    expected_value?: number;
    actual_value?: number;
    deviation_percentage?: number;
    z_score?: number;
  };
}

export interface DataQualityReport {
  record_id: number;
  overall_score: number;
  completeness_score: number;
  accuracy_score: number;
  consistency_score: number;
  validity_score: number;
  timeliness_score: number;
  issues: string[];
  recommendations: string[];
  generated_at: string;
}

export interface ValidationConfig {
  enable_real_time_validation: boolean;
  enable_batch_validation: boolean;
  enable_anomaly_detection: boolean;
  auto_resolve_low_severity: boolean;
  anomaly_sensitivity: number;
  notification_threshold: ValidationSeverity;
  retention_period_days: number;
}

export type ValidationType = 
  | 'range_check'
  | 'required_check'
  | 'format_check'
  | 'consistency_check'
  | 'business_rule'
  | 'statistical_anomaly'
  | 'completeness_check'
  | 'duplicate_check';

export type ValidationSeverity = 'low' | 'medium' | 'high' | 'critical';

export type AnomalyType =
  | 'statistical_outlier'
  | 'trend_deviation'
  | 'pattern_anomaly'
  | 'data_quality_issue'
  | 'compliance_violation'
  | 'system_error'
  | 'fraud_detection'
  | 'seasonal_anomaly'
  | 'sudden_change'
  | 'missing_data';

export interface ValidationSchema {
  rules: ValidationRule[];
}

// ============================================================================
// DATA VALIDATION SERVICE CLASS
// ============================================================================

class DataValidationService {
  /**
   * Get all validation rules from API
   */
  public async getValidationRules(): Promise<ValidationRule[]> {
    try {
      return await dataValidationApi.getValidationRules();
    } catch (error) {
      console.error('[DataValidationService] Failed to get validation rules:', error);
      // Return default rules on error
      return this.getDefaultRules();
    }
  }

  /**
   * Get a single validation rule by ID
   */
  public async getValidationRule(ruleId: string): Promise<ValidationRule | undefined> {
    try {
      return await dataValidationApi.getValidationRule(ruleId) || undefined;
    } catch (error) {
      console.error('[DataValidationService] Failed to get validation rule:', error);
      return undefined;
    }
  }

  /**
   * Add a new validation rule via API
   */
  public async addValidationRule(rule: ValidationRule): Promise<boolean> {
    try {
      await dataValidationApi.createValidationRule(rule);
      return true;
    } catch (error) {
      console.error('[DataValidationService] Failed to add validation rule:', error);
      return false;
    }
  }

  /**
   * Update an existing validation rule via API
   */
  public async updateValidationRule(ruleId: string, updates: Partial<ValidationRule>): Promise<boolean> {
    try {
      await dataValidationApi.updateValidationRule(ruleId, updates);
      return true;
    } catch (error) {
      console.error('[DataValidationService] Failed to update validation rule:', error);
      return false;
    }
  }

  /**
   * Delete a validation rule via API
   */
  public async deleteValidationRule(ruleId: string): Promise<boolean> {
    try {
      await dataValidationApi.deleteValidationRule(ruleId);
      return true;
    } catch (error) {
      console.error('[DataValidationService] Failed to delete validation rule:', error);
      return false;
    }
  }

  /**
   * Get all anomalies from API
   */
  public async getAnomalies(): Promise<AnomalyDetection[]> {
    try {
      return await dataValidationApi.getAnomalies();
    } catch (error) {
      console.error('[DataValidationService] Failed to get anomalies:', error);
      return [];
    }
  }

  /**
   * Get a single anomaly by ID
   */
  public async getAnomaly(anomalyId: string): Promise<AnomalyDetection | undefined> {
    try {
      return await dataValidationApi.getAnomaly(anomalyId) || undefined;
    } catch (error) {
      console.error('[DataValidationService] Failed to get anomaly:', error);
      return undefined;
    }
  }

  /**
   * Add a new anomaly via API
   */
  public async addAnomaly(anomaly: AnomalyDetection): Promise<boolean> {
    try {
      await dataValidationApi.createAnomaly(anomaly);
      return true;
    } catch (error) {
      console.error('[DataValidationService] Failed to add anomaly:', error);
      return false;
    }
  }

  /**
   * Resolve an anomaly via API
   */
  public async resolveAnomaly(anomalyId: string, notes: string): Promise<boolean> {
    try {
      await dataValidationApi.resolveAnomaly(anomalyId, notes);
      return true;
    } catch (error) {
      console.error('[DataValidationService] Failed to resolve anomaly:', error);
      return false;
    }
  }

  /**
   * Get validation configuration from API
   */
  public async getConfig(): Promise<ValidationConfig> {
    try {
      return await dataValidationApi.getConfig();
    } catch (error) {
      console.error('[DataValidationService] Failed to get config:', error);
      return this.getDefaultConfig();
    }
  }

  /**
   * Update validation configuration via API
   */
  public async updateConfig(updates: Partial<ValidationConfig>): Promise<boolean> {
    try {
      await dataValidationApi.updateConfig(updates);
      return true;
    } catch (error) {
      console.error('[DataValidationService] Failed to update config:', error);
      return false;
    }
  }


  /**
   * Get default validation rules (fallback)
   */
  private getDefaultRules(): ValidationRule[] {
    return [
      {
        id: 'rule_001',
        name: 'Emission Value Range Check',
        description: 'Ensure emission values are within acceptable range',
        type: 'range_check',
        field_types: ['number'],
        parameters: { min: 0, max: 1000000 },
        is_active: true,
        severity: 'high',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 'rule_002',
        name: 'Required Field Check',
        description: 'Ensure all required fields are present',
        type: 'required_check',
        field_types: ['string', 'number', 'date'],
        parameters: {},
        is_active: true,
        severity: 'critical',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 'rule_003',
        name: 'Date Format Validation',
        description: 'Validate date format consistency',
        type: 'format_check',
        field_types: ['date'],
        parameters: { format: 'YYYY-MM-DD' },
        is_active: true,
        severity: 'medium',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ];
  }

  /**
   * Get default validation configuration (fallback)
   */
  private getDefaultConfig(): ValidationConfig {
    return {
      enable_real_time_validation: true,
      enable_batch_validation: true,
      enable_anomaly_detection: true,
      auto_resolve_low_severity: false,
      anomaly_sensitivity: 0.7,
      notification_threshold: 'medium',
      retention_period_days: 90
    };
  }
}

// Singleton instance
const dataValidationService = new DataValidationService();

export { dataValidationService };
export default dataValidationService;
